import * as React from 'react';
import { NewCard, NewCardProps } from 'wix-ui-tpa/NewCard';
import {
  Button,
  PRIORITY as ButtonPriority,
  SIZE as ButtonSize,
} from 'wix-ui-tpa/Button';
import { wrapper } from '@wix/challenges-web-api/dist/src/interfaces/generated/api';
import Challenge = wrapper.com.wixpress.achievements.api.v1.Challenge;
import styles from './ChallengeCard.st.css';
import {
  TextAlignment,
  ImageShape,
  ImageRatio,
  CropSelection,
} from '../Settings/challengeSettings/challengeSettings.types';
import { translate, ITranslationProps } from 'react-i18next';
import { imageDataToMediaUrl } from '@wix/challenges-web-library/dist/src/helpers/image';
import { IImageData } from '@wix/challenges-web-library/dist/src/types';
import { serverPricingToClientPriceAndPaidPlansString } from '@wix/challenges-web-library/dist/src/formatters/serverClient/price';
import { serverTimelineTypeToClientTimelineString } from '@wix/challenges-web-library/dist/src/formatters/serverClient/timelineType';
import get from 'lodash/get';

type ChallengeItem = Challenge.View;

interface IChallengeCard extends NewCardProps {
  challenge: ChallengeItem;
  goTo(any): void;
  displayParams?: {
    image: boolean;
    duration: boolean;
    participants: boolean;
    divider: boolean;
    price: boolean;
  };
  textAlignment: TextAlignment;
  imageShape: ImageShape;
  imageRatio: ImageRatio;
  cropSelection: CropSelection;
  isMobile: boolean;
  buttonState: 'default' | 'hover';
}

interface IChallengeCardState {
  cardWidth: number;
}

class ChallengeCardComponent extends React.Component<
  IChallengeCard & ITranslationProps,
  IChallengeCardState
> {
  static displayName = 'ChallengeCard';
  private readonly cardRef = React.createRef<HTMLDivElement>();

  state = {
    cardWidth: 0,
  };

  componentDidUpdate(_prevProps, prevState: IChallengeCardState) {
    if (this.cardRef.current.offsetWidth !== prevState.cardWidth) {
      this.setState({ cardWidth: this.cardRef.current.offsetWidth || 0 });
    }
  }

  render() {
    const {
      challenge,
      userPaidPlans,
      displayParams,
      goTo,
      textAlignment,
      imageShape = ImageShape.Rectangle,
      imageRatio,
      cropSelection,
      locale,
      isMobile,
      buttonState,
      t,
      ...rest
    } = this.props;
    const { cardWidth } = this.state;

    const cardHeight =
      imageShape === ImageShape.Rectangle || imageShape === ImageShape.Round
        ? imageRatio === ImageRatio.HD
          ? (cardWidth * 9) / 16
          : (cardWidth * 3) / 4
        : null;

    const isSingleKey =
      get(challenge.settings.timelineType, 'flexible.duration.value', 0) ===
      '1';
    const durationString = serverTimelineTypeToClientTimelineString(
      challenge.settings.timelineType,
      locale,
      t,
      'challenge-card.duration-string.ongoing',
      isSingleKey
        ? 'challenge-card.duration-string.flexible.days'
        : 'challenge-card.duration-string.flexible.days_plural',
      isSingleKey
        ? 'challenge-card.duration-string.flexible.weeks'
        : 'challenge-card.duration-string.flexible.weeks_plural',
    );
    const pricingString = serverPricingToClientPriceAndPaidPlansString(
      challenge.settings.pricing,
      userPaidPlans,
      t,
      'challenge-card.pricing.free',
      'challenge-card.pricing.paid.separator',
    );
    const showParticipants = !!(
      displayParams.participants && challenge.participantsNumber
    );

    return (
      <NewCard
        {...styles(
          styles.card,
          {
            textAlignment,
            imageShape,
            cropSelection,
            mobile: isMobile,
            buttonState,
          },
          rest,
        )}
        stacked={true}
      >
        {displayParams.image && (
          <NewCard.Container className={styles.media}>
            <div
              data-hook="image-wrapper"
              className={styles.imageWrapper}
              style={{
                ...(imageShape === ImageShape.Round && {
                  width: `${cardHeight - 32}px`,
                  height: `${cardHeight - 32}px`,
                }),
                ...(imageShape === ImageShape.Square && {
                  height: `${cardWidth || 164.5}px`,
                }),
                ...(imageShape === ImageShape.Rectangle && {
                  height: `${cardHeight || 164.5}px`,
                }),
                ...(challenge.settings.description.media && {
                  backgroundImage: `url(${imageDataToMediaUrl(
                    challenge.settings.description.media as IImageData,
                  )})`,
                }),
              }}
            ></div>
          </NewCard.Container>
        )}
        <NewCard.Container className={styles.info}>
          <div
            data-hook="info-wrapper"
            ref={this.cardRef}
            style={{ position: 'absolute', width: '100%' }}
          />
          <div className={styles.infoWrapper}>
            <h2 className={styles.title} data-hook="card-title">
              {challenge.settings.description.title}
            </h2>
            {(displayParams.duration || showParticipants) && (
              <p className={styles.subtitle} data-hook="card-subtitle">
                {!!displayParams.duration && (
                  <span
                    className={styles.subtitleItem}
                    data-hook="challenge-duration"
                  >
                    {durationString}
                  </span>
                )}
                {displayParams.duration && showParticipants && (
                  <span
                    className={styles.subtitleItem}
                    data-hook="challenge-bullet"
                  >
                    {<>&nbsp;&bull;&nbsp;</>}
                  </span>
                )}
                {!!showParticipants && (
                  <span
                    className={styles.subtitleItem}
                    data-hook="challenge-participants"
                  >
                    {t(
                      `challenge-card.${
                        challenge.participantsNumber > 1
                          ? 'participants_plural'
                          : 'participants'
                      }`,
                      {
                        count: challenge.participantsNumber,
                      },
                    )}
                  </span>
                )}
              </p>
            )}
            {displayParams.divider && (
              <div data-hook="challenge-divider" className={styles.divider} />
            )}
            {displayParams.price && (
              <p data-hook="challenge-pricing" className={styles.pricing}>
                {pricingString}
              </p>
            )}
          </div>

          <div className={styles.buttonWrapper}>
            <Button
              data-hook="view-button"
              fullWidth={cardWidth > 300 ? false : true}
              className={styles.button}
              priority={ButtonPriority.primary}
              size={ButtonSize.small}
              onClick={() => {
                goTo(challenge.id);
              }}
            >
              {t('challenge-card.view-button')}
            </Button>
          </div>
        </NewCard.Container>
      </NewCard>
    );
  }
}

export const ChallengeCard = translate()(ChallengeCardComponent);
